import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createSlots as _createSlots, createBlock as _createBlock, resolveComponent as _resolveComponent, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "query-builder-rule-selected-searchType" }
const _hoisted_3 = { class: "criteria-manipulation" }
const _hoisted_4 = {
  key: 0,
  class: "preselection-list"
}
const _hoisted_5 = { class: "rule-ops-value" }
const _hoisted_6 = {
  key: 0,
  class: "query-builder-rule-ops-selection"
}
const _hoisted_7 = { class: "query-builder-rule-value-selection" }
const _hoisted_8 = { class: "criteria-meta-info" }
const _hoisted_9 = { class: "query-builder-rule-language-selection" }
const _hoisted_10 = { class: "query-builder-rule-actions" }

import MSelect from "@/BasicComponentLibrary/MSelect.vue";
import { getTranslatedSelectionOpsList } from "@AdvancedSearch/Util/opsLookUp";
import Icon from "@common/components/Icon.vue";
import UserProfileSelectorItem from "@common/components/UserProfileSelectorItem.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { AllowedOps, FetchableData, QueryBuilderRule } from "@medapro-types/AdvancedSearch/components/QueryBuilder";
import { RootState } from "@medapro-types/common/rootstate";
import { Guid } from "@util/guid";
import { naturalSort } from "@util/naturalSort";
import _ from "lodash";
import { Ref, computed, inject, nextTick, onMounted, reactive, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useState } from "vuex-composition-helpers";
import AttributeGroupRule from "./QueryRules/AttributeGroupRule.vue";
import BoolRule from "./QueryRules/BoolRule.vue";
import CatalogPathRule from "./QueryRules/CatalogPathRule.vue";
import DateRule from "./QueryRules/DateRule.vue";
import DateTimeRule from "./QueryRules/DateTimeRule.vue";
import DropDownRule from "./QueryRules/DropDownRule.vue";
import EditableDropDownRule from "./QueryRules/EditableDropDownRule.vue";
import NumberRule from "./QueryRules/NumberRule.vue";
import PreselectionRule from "./QueryRules/PreselectionRule.vue";
import StringRule from "./QueryRules/StringRule.vue";

//#region General
type ViewModel = {
  id: string;
  localModel: QueryBuilderRule;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'Rule',
  props: {
    value: {},
    path: {}
  },
  emits: ["input", "deleteRule", "refreshBackground"],
  setup(__props: any, { emit: emits }) {

const props = __props;

const { t: $t } = useI18n();

const { metadata } = useState<RootState>(["metadata"]);

//#endregion

//#region ViewModel
const vm = reactive<ViewModel>({
  id: props.value.ruleId ?? Guid.new(),
  localModel: props.value
});

const isUserRuleType = computed(() => {
  return vm.localModel.searchType === "assignedTo" || vm.localModel.searchType === "createdBy" || vm.localModel.searchType === "changedBy";
});

const isDateBoundarySearchType = computed(() => {
  return vm.localModel.searchType === "validFrom" || vm.localModel.searchType === "validTo";
});

//#endregion

//#region FetchedData
const fetchedData = inject<Ref<FetchableData>>("fetchedData");
const dataConfig = computed(() => unref(fetchedData)?.find((x) => x.searchType === vm.localModel.searchType));
const { localizedLanguages } = useContentLanguages();
//#endregion

function isNegatedOp(op: string | null | undefined): boolean {
  if (op == null) return false;
  return op.toLowerCase().includes("not");
}

//#region Update vModel
watch(
  vm.localModel,
  (nv) => {
    emits("input", nv);
  },
  { immediate: true, deep: true }
);
//#endregion

//#region Rule Value Selection
const isSpecialRule = computed(() => {
  return dataConfig.value?.uiType == "preselectTree";
});

const validatedOperators = computed(() => {
  // currently only necessary if there is a language-dependent rule
  if (dataConfig?.value?.languageDependent === false) {
    return operators.value;
  }
  const areNegatedOperatorsDisabled = vm.localModel.dataLanguage === "";
  return areNegatedOperatorsDisabled ? operators.value.filter((obj) => !isNegatedOp(obj.id)) : operators.value;
});

const operators = computed(() => {
  if (dataConfig.value?.uiType && dataConfig.value?.uiType != "preselectList") {
    if (dataConfig.value?.uiType === "date") {
      if (props.value.value === "") return getTranslatedSelectionOpsList(dataConfig.value?.uiType).filter((o) => o.id === "equals" || o.id === "equalsNot");
    }
    return getTranslatedSelectionOpsList(dataConfig.value?.uiType);
  }
  const foundEntry = getPreselectionEntry();
  return foundEntry === "Double" ? getTranslatedSelectionOpsList("numeric") : getTranslatedSelectionOpsList("alphanumeric");
});

const getValueSelectionComponentByType = () => {
  let foundEntry;
  switch (dataConfig.value?.uiType) {
    case "selectionList":
      return DropDownRule;
    case "searchableList":
      return EditableDropDownRule;
    case "numeric":
      return NumberRule;
    case "logical":
      return BoolRule;
    case "date":
      return DateRule;
    case "datetime":
      return DateTimeRule;
    case "catalogNode":
      return CatalogPathRule;
    case "attributeGroup":
      return AttributeGroupRule;
    case "preselectList":
      foundEntry = getPreselectionEntry();
      return foundEntry === "Double" ? NumberRule : StringRule;
    default:
      return StringRule;
  }
};
//#endregion

//#region PreselectionEntry
function getPreselectionEntry() {
  const foundEntry = dataConfig.value?.selection.find((x) => x[currentIdFieldName.value ?? "id"] == vm.localModel.attributeId);
  return !foundEntry ? null : foundEntry.dataType;
}
//#region Languages
const languageSelection = computed(() => {
  const result = [
    ...(unref(localizedLanguages)?.map((x) => ({
      id: x.languageCode,
      name: x.localizedName
    })) ?? [])
  ];

  if (!isNegatedOp(vm.localModel.operator)) {
    result.unshift({ id: "", name: `<${$t("inAnyLanguage")}>` });
  }

  return result;
});
//#endregion

//#region PreselectionList

const sortedSelectionList = computed(() => {
  const allValue: Record<string, string | null> = { name: `<${$t("all")}>` };
  const idKey = unref(currentIdFieldName);

  if (idKey) allValue[idKey] = null;

  return dataConfig.value ? [allValue, ...naturalSort(dataConfig.value?.selection, (x) => x.name)] : [];
});

const currentIdFieldName = computed<string | null>(() => {
  if (dataConfig.value?.uiType === "preselectList") {
    const firstElement = _.first(dataConfig.value?.selection);
    if (firstElement) {
      const foundIdName = Object.keys(firstElement).find((x) => x.endsWith("Id"));
      if (foundIdName) return foundIdName;
    }
  }
  return null;
});

//#endregion

//#region LanguageSelection
watch(
  () => dataConfig.value?.languageDependent,
  (nv) => {
    if (nv && vm.localModel.dataLanguage === undefined) {
      vm.localModel.dataLanguage = metadata?.value?.contentLanguages.enabled[0].languageCode;
    }
  },
  { immediate: true }
);
//#endregion

//#region LifeCycle

onMounted(() => {
  if (!vm.localModel.ruleId) {
    vm.localModel.ruleId = vm.id;
  }

  nextTick(() => {
    if (!vm.localModel.operator && dataConfig.value?.uiType != "preselectTree") {
      vm.localModel.operator = (_.first(operators.value)?.id ?? "equals") satisfies AllowedOps;
    }

    if (dataConfig.value?.uiType === "preselectList" && !vm.localModel.attributeId) {
      const firstElement = _.first(sortedSelectionList.value);
      if (firstElement) {
        const foundIdName = Object.keys(firstElement).find((x) => x.endsWith("Id"));
        if (foundIdName) vm.localModel.attributeId = firstElement[foundIdName];
      }
    }
  });
});

//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_btn = _resolveComponent("b-btn")!

  return (dataConfig.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["query-builder-rule", { 'query-builder-rule-lg': isSpecialRule.value }]),
        id: vm.id
      }, [
        (!isSpecialRule.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("div", _hoisted_2, _toDisplayString(_unref($t)("AdvancedSearch.SearchType." + vm.localModel.searchType)), 1),
              _createElementVNode("div", _hoisted_3, [
                (dataConfig.value?.uiType === 'preselectList')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createVNode(MSelect, {
                        modelValue: vm.localModel.attributeId,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((vm.localModel.attributeId) = $event)),
                        items: sortedSelectionList.value,
                        textField: "name",
                        "value-field": currentIdFieldName.value ?? 'id',
                        "data-cy": "rule-preselection"
                      }, null, 8, ["modelValue", "items", "value-field"])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_5, [
                  (operators.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _createVNode(MSelect, {
                          class: "w-100",
                          modelValue: vm.localModel.operator,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((vm.localModel.operator) = $event)),
                          items: validatedOperators.value,
                          "value-field": 'id',
                          "data-cy": "rule-operator"
                        }, null, 8, ["modelValue", "items"])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_7, [
                    (_openBlock(), _createBlock(_resolveDynamicComponent(getValueSelectionComponentByType()), {
                      modelValue: vm.localModel.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((vm.localModel.value) = $event)),
                      items: dataConfig.value?.selection,
                      "can-be-none": isDateBoundarySearchType.value,
                      onDeleteRule: _cache[3] || (_cache[3] = ($event: any) => (emits('deleteRule', { id: vm.id }))),
                      class: _normalizeClass({ 'advanced-search-user-selector': isUserRuleType.value })
                    }, _createSlots({ _: 2 }, [
                      (isUserRuleType.value)
                        ? {
                            name: "default",
                            fn: _withCtx(({ entry }) => [
                              _createVNode(UserProfileSelectorItem, {
                                username: entry.name,
                                id: entry.userId,
                                "has-image": entry.hasImage
                              }, null, 8, ["username", "id", "has-image"])
                            ]),
                            key: "0"
                          }
                        : undefined,
                      (isUserRuleType.value)
                        ? {
                            name: "button-content",
                            fn: _withCtx(({ entry }) => [
                              _createVNode(UserProfileSelectorItem, {
                                username: entry?.name,
                                id: entry?.userId,
                                "has-image": entry.hasImage
                              }, null, 8, ["username", "id", "has-image"])
                            ]),
                            key: "1"
                          }
                        : undefined
                    ]), 1064, ["modelValue", "items", "can-be-none", "class"]))
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  (dataConfig.value?.languageDependent)
                    ? (_openBlock(), _createBlock(MSelect, {
                        key: 0,
                        modelValue: vm.localModel.dataLanguage,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((vm.localModel.dataLanguage) = $event)),
                        items: languageSelection.value,
                        textField: "name",
                        valueField: "id",
                        "data-cy": "rule-language"
                      }, null, 8, ["modelValue", "items"]))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(_component_b_btn, {
                    class: "btn-icon mono",
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (emits('deleteRule', { id: vm.id }))),
                    "data-cy": "rule-clear"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(Icon, { icon: "Close" })
                    ]),
                    _: 1
                  })
                ])
              ])
            ], 64))
          : (_openBlock(), _createBlock(PreselectionRule, {
              key: 1,
              modelValue: vm.localModel,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((vm.localModel) = $event)),
              "language-required": !!dataConfig.value?.languageDependent,
              "additional-operator-support": dataConfig.value,
              onDeleteRule: _cache[7] || (_cache[7] = ($event: any) => (emits('deleteRule', { id: vm.id })))
            }, null, 8, ["modelValue", "language-required", "additional-operator-support"]))
      ], 10, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})